// useAgentLifecycle — the owner's on/off switch for one agent.
//
// Blueprint agent-lifecycle.md §2.4: disable is the default way to stop an
// agent and delete is the advanced one, so disabling is a single click with
// its undo in the toast, and only delete keeps a confirmation.
//
// Enabling can be refused: the schema will not reactivate an agent holding the
// unrestricted '*' grant. The refusal is the server's to word, because the way
// out of that state is a permission write on a rail this hook does not own, so
// the failure is rendered verbatim rather than paraphrased into a remedy that
// would drift from it.
import { useCallback, useRef, useState } from 'react';
import { setAgentDisabled, type Agent } from '../lib/api';

export type LifecyclePending = 'disable' | 'enable' | null;

export interface AgentLifecycle {
  disabled: boolean;
  pending: LifecyclePending;
  /** Failure of the disable click, rendered inside the lifecycle section. */
  disableError: string | null;
  /** Failure of an enable, rendered under the page banner. */
  enableError: string | null;
  disable: () => Promise<void>;
  enable: () => Promise<void>;
  dismissEnableError: () => void;
}

// request() rejects with ApiError and a dead network rejects with TypeError,
// so every rejection reaching here is an Error and carries its own message.
const errorText = (err: unknown): string =>
  err instanceof Error ? err.message : String(err);

interface Options {
  agentId: string;
  agent: Agent;
  /** Publishes authoritative server state back to whoever renders the agent. */
  onUpdated: (agent: Agent) => void;
  /** Toast copy; the banner carries the durable undo, this is the cheap one. */
  onToast: (message: string, action?: { label: string; onClick: () => void }) => void;
}

export function useAgentLifecycle({ agentId, agent, onUpdated, onToast }: Options): AgentLifecycle {
  const [pending, setPending] = useState<LifecyclePending>(null);
  const [disableError, setDisableError] = useState<string | null>(null);
  const [enableError, setEnableError] = useState<string | null>(null);
  // The toast's undo action closes over the render that existed before disable()
  // started, where `pending` was still null. The guard therefore has to be a
  // shared cell rather than that captured value, or the undo fires a second
  // write on top of the disable it is undoing.
  const inFlight = useRef(false);

  const disabled = agent.disabled;

  const enable = useCallback(async () => {
    if (inFlight.current) return;
    try {
      inFlight.current = true;
      setPending('enable');
      setEnableError(null);
      onUpdated(await setAgentDisabled(agentId, false));
      onToast(`${agent.display_name} enabled`);
    } catch (err) {
      setEnableError(errorText(err));
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  }, [agent.display_name, agentId, onToast, onUpdated]);

  const disable = useCallback(async () => {
    if (inFlight.current) return;
    try {
      inFlight.current = true;
      setPending('disable');
      setDisableError(null);
      onUpdated(await setAgentDisabled(agentId, true));
      onToast(`${agent.display_name} disabled`, { label: 'Enable', onClick: () => { void enable(); } });
    } catch (err) {
      setDisableError(errorText(err));
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  }, [agent.display_name, agentId, enable, onToast, onUpdated]);

  const dismissEnableError = useCallback(() => setEnableError(null), []);

  return {
    disabled,
    pending,
    disableError,
    enableError,
    disable,
    enable,
    dismissEnableError,
  };
}
